import { Avatar, Image, Text,Alert, AlertDescription, AlertIcon, Box, Button, CloseButton, Link, Flex, HStack, useDisclosure, useToast, Popover, PopoverTrigger, Portal, PopoverContent, PopoverBody } from "@chakra-ui/react";
import React, { Dispatch, useContext, useEffect, useState } from "react";

import Map, {
  Marker, NavigationControl, ScaleControl,
  GeolocateControl,
  Source,
  Layer, MapRef
} from 'react-map-gl';
import { generateRandomPath } from "@/app/helpers/generateRandomPathHelper";
import NextLink from 'next/link';
import { useTranslation } from "next-i18next";
import { GlobalDataContext } from "@/app/context/global/globalDataContext";
import { GlobalDataUserContext } from "@/app/context/global/globalDataUserContext";
import { MapType } from "@/app/types/mapType";
import { AuthenticationContext } from "@/app/context/authentication/authenticationContext";
import ElevationCanvas from "./elevationCanvas";
import { MdInfo } from "react-icons/md";
import { TbRubberStamp } from "react-icons/tb";
import { NotificationContext } from "@/app/context/notification/notificationContext";
import { calculateBearing } from "@/app/helpers/mapHelper";
import { NoGeolocalisationModal } from "../game/modal/noGeolocalisationModal";
const position = { lat: 50.3945761, lng: 5.9302925};
const position2 = { lat: 50.395102, lng: 5.9296072};
const easyThreshold = 150; // en mètres
const mediumThreshold = 250; // en mètres

 
type Props = {
  mapContent?: React.ReactElement
  handleClick?: any 
  startLocation? : google.maps.LatLngLiteral|undefined
  doResize?: boolean
  mapRef?: React.MutableRefObject<MapRef | null>
  pitch?: number
  forceZoom?: boolean
  handleMoveEnd?: any
  handleZoomEnd?: any
  handleLoaded?: any
};

export default function MapboxMap({mapContent, handleClick, startLocation, doResize, mapRef, pitch, forceZoom, handleMoveEnd, handleZoomEnd, handleLoaded}:Props) {
  const {t} = useTranslation();
  //const mapRef = React.useRef<MapRef | null>(null);
  const [generateFromCoordinates, setGenerateFromCoordinates] = useState<any>(null)
  const [coords, setCoords] = useState<any>([])
  const [distance, setDistance] = useState(0)
  const [duration, setDuration] = useState(0)
  const [markers, setMarkers] = useState([])
  const [isReady, setIsReady] = useState(true)
  const [difficulty, setDifficulty] = useState<string>("easy");
  const [elevations, setElevations] =useState<number[]>([])
  const [totalGain, setTotalGain] = useState(0)
  const [allowBearing, setAllowBearing] = useState(true)
  const [mapForceCenter, setMapForceCenter] = useState(false)
  const [mapStyle, setMapStyle] = useState('mapbox://styles/mapbox/light-v11');

  const { isOpen:isOpenGeoloc, onOpen:onOpenGeoloc, onClose:onCloseGeoloc } = useDisclosure()

  const updateMapStyle = () => {
    const hour = new Date().getHours();
    if (hour >= 18 || hour < 6) {
      setMapStyle('mapbox://styles/mapbox/dark-v11');
    } else {
      setMapStyle('mapbox://styles/mapbox/light-v11');
    }
  };

  useEffect(() => {
    // Mettre à jour le style au premier rendu
    updateMapStyle();

    // Configurer un intervalle pour vérifier l'heure toutes les minutes
    const interval = setInterval(() => {
      updateMapStyle();
    }, 60000); // 60000 ms = 1 minute

    // Nettoyer l'intervalle quand le composant est démonté
    return () => clearInterval(interval);
  }, []);

  const {
    isOpen: isVisible,
    onClose,
    onOpen,
  } = useDisclosure({ defaultIsOpen: true })

  const {
    user
} = useContext( AuthenticationContext)


  const {
    language,
    location,
    bearing
} = useContext(GlobalDataContext)   
/*
  const {
    //location,
    context,
    perimeterCoordinates,
    setPerimeterCoordinates,
    maps,
    setMaps,
    isMapLoading
  } = useContext(GameDataUserContext)

  const {
    objects,
    setObjects,
    contactId,
    geoLocTransposed,
    //geoLocTransposable,
    setGeoLocTransposed
  } = useContext(GameDataContext)
*/

  let geoLocTransposable = true

  const {
    config,
    setConfig,
    allPlayersPositions
  } = useContext(GlobalDataUserContext)

  const {
    addError
  }=useContext(NotificationContext)

  const setGenerationCoordinates = (coord:any) => {
    setGenerateFromCoordinates(coord)
  }

  const calculateElevationGain=(elevations:any) =>{
    let totalGain = 0;
    let totalLoss = 0;
  
    for (let i = 1; i < elevations.length; i++) {
      const diff = elevations[i] - elevations[i - 1];
      if (diff > 0) {
        totalGain += diff;
      } else {
        totalLoss += Math.abs(diff);
      }
    }
  
    return { totalGainCalc:totalGain, totalLoss };
  }

  const getCenter=(points:any) =>{
    let sumLat = 0;
    let sumLng = 0;
  
    for (const point of points) {
      sumLat += point.lat;
      sumLng += point.lng;
    }
  
    return {lat: sumLat / points.length, lng:sumLng / points.length};
  }

  const getRoute = async(coord1:any,coord2:any,coord3:any)=>{
    const options = {
      method: "POST",
      headers: {
          "Content-type": "application/json",
          'Authorization': "Bearer " + await user?.getIdToken()
      },
      body: JSON.stringify({ coord1: coord1, coord2: coord2, coord3: coord3})
    }
    const response = await fetch(`/api/get_route`, options);
 
    try {
      
      const data = await response.json()
      const coords = data.routes[0].geometry.coordinates
      if ( !coords || !coords.length ){
        throw new Error("No coordinates")
      }
      setDistance(data.routes[0].distance)
      setDuration(data.routes[0].duration)
      setCoords(coords)
      if ( mapRef && mapRef.current) {
        mapRef.current.getMap().setCenter(getCenter([coord1,coord2,coord3]));
      }
      setElevations(data.elevation)
      const { totalGainCalc } = calculateElevationGain(data.elevation);
      setTotalGain(totalGainCalc)
      if (totalGainCalc < easyThreshold) {
        setDifficulty('easy')
      } else if (totalGainCalc < mediumThreshold) {
        setDifficulty('medium')
      } else {
        setDifficulty('hard')
      }

    }catch(e){
      setGenerateFromCoordinates(null)
      addError(t("transposable.unableToRegenerate"),"error")
    }
  }

  const transposedCoords = () => {
    /*if ( coords && coords.length ){
      const totalCoords = coords.length
      let count = 0;
      for (let key in objects) {
        if (objects[key].type === "map") {
          count++;
        }
      }
      const tempMapType = [] as MapType[]
      objects.forEach((item:any)=>{
        if ( item.type == "map"){
          let index= parseInt(item.name as string)
          const newItem = item
          const tempCoord = coords[Math.round(totalCoords*(index-1)/(count))]
          if ( tempCoord && tempCoord[1] && tempCoord[0]){
            newItem.coord = {lat: tempCoord[1], lng: tempCoord[0]}
            //newItem.dependOn = "*"
          }
          tempMapType.push(newItem)
        }
      })

      if (tempMapType && tempMapType.length){
        setMaps(tempMapType)
        setGeoLocTransposed(true)
      }      
    }*/
  }

  /*useEffect(()=>{
    const markersTmp:any = []
    if ( !geoLocTransposable && objects && objects.length ){
      objects.forEach((item)=>{
        if ( item.type == "map"){
          if ( item.coord && item.coord.lat && item.coord.lng && (item.dependOn == "*" || (context && context.length && context.find((itemContext)=>itemContext.code==item.dependOn) ))){
            markersTmp.push ({
              "name": item.name,
              "coord": item.coord
            })
          }
        }
      })
      if ( markersTmp && markersTmp.length ){
        setMarkers(markersTmp)
      }
    }  
  },[objects, context])*/

  /*useEffect(()=>{
    const markersTmp:any = []
    if ( geoLocTransposable && maps && maps.length ){
      maps.forEach((item)=>{
        if ( item.type == "map"){
            if ( ((context && context.length && context.find((itemContext)=>itemContext.code==item.dependOn) ))){
              markersTmp.push ({
                "name": item.name,
                "coord": item.coord,
                "color": "#693"
              })
            }else{
              markersTmp.push ({
                "name": item.name,
                "coord": item.coord,
                "color": "red"
              })
            }
        }
      })
      if ( markersTmp && markersTmp.length ){
        setMarkers(markersTmp)
      }
    }  
  },[maps, context])*/

  const handleResize:any = () => {
    if ( mapRef && mapRef.current) {
      mapRef.current.getMap().resize();
    }
  };

  useEffect(()=>{
    if ( doResize ){
      handleResize()
    }
  },[doResize])

  useEffect(()=>{
    
    window.addEventListener('resize', handleResize);
    handleResize()

    setIsReady(true)

    // Supprimer l'écouteur d'événement lorsque le composant est démonté
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  },[])

  useEffect(()=>{
    if ( mapRef && mapRef.current) {
      mapRef.current.getMap().resize();
      if ( startLocation ) {
        mapRef.current.getMap().setCenter(startLocation)
      }
    }
  },[mapRef, startLocation])

 /* useEffect(()=>{
    if (  geoLocTransposed){
      handleResize()
    }
    //console.log("istransposed",geoLocTransposed)
  },[geoLocTransposed])

  useEffect(()=>{
    if ( contactId && mapRef && mapRef.current) {
      mapRef.current.getMap().resize();
      if ( location ) {
        mapRef.current.getMap().setCenter(location)
      }
    }
  },[contactId])*/

  const geoJson: GeoJSON.FeatureCollection  = {
    "type": "FeatureCollection",
    "features": [
      {
        "type": "Feature",
        "geometry": {
          "type": "LineString",
          "coordinates": [...coords]
        },
        properties: {},
      }
    ]
  }

  useEffect(()=>{
    if ( mapRef && mapRef.current && location && mapForceCenter) {
        mapRef.current.getMap().easeTo({
          center:location,
          //bearing:bearing,
          duration: 2000, // Durée de l'animation en ms
          easing: (t) => t * (2 - t) // Fonction d'assouplissement pour une animation fluide
        });
    }
  },[mapRef, location,mapForceCenter])

  const closeTransposed= ()=>{
    if ( mapRef && mapRef.current) {
      mapRef.current.getMap().resize();
    }
  }
/*
  useEffect(()=>{
    const mapInstance = mapRef?.current?.getMap();

    if (mapInstance) {
      const handleMoveEnd = () => {
        const currentBearing = mapInstance.getBearing();
        if (allowBearing && currentBearing !== undefined) {
          
          setBearing(currentBearing);
        }
      };

      mapInstance.on('moveend', handleMoveEnd);

      // Cleanup listener on unmount or effect re-run
      return () => {
        mapInstance.off('moveend', handleMoveEnd);
      };
    }
  },[allowBearing, mapRef])
*/

  useEffect(()=>{
    if ( mapRef && mapRef.current && forceZoom) {
      mapRef.current.getMap().easeTo({
        center:location,
        zoom: 19, // Niveau de zoom cible
        pitch: pitch, // Inclinaison cible
        duration: 750, // Durée de l'animation en ms
        //bearing:bearing,
        easing: (t) => t * (2 - t) // Fonction d'assouplissement pour une animation fluide
      });
    }
  },[mapRef,forceZoom])

  const handle=(e:any)=>{
    console.log("trackUserLocationStart",e)
    setMapForceCenter(true)
  }
  return (
    <Flex h="full" w="full" direction="column" position="relative" p={0} color="white">
      {location && isReady && <Map
        ref={mapRef}
        initialViewState={{          
          latitude: startLocation?.lat??location?.lat,
          longitude: startLocation?.lng??location?.lng,
          pitch:pitch,
          zoom: 16
        }}
        //bearing={bearing}
        style={{width:"100%", height:"100%", "maxWidth": "100%", "maxHeight": "100%"}}
        mapStyle={mapStyle}
        mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN as string}  
        onClick={handleClick}  
        onMoveEnd={handleMoveEnd??(()=>{})}  
        onZoomEnd={handleZoomEnd??(()=>{})}
        onLoad={handleLoaded??(()=>{})}
        onDrag={()=>setMapForceCenter(false)}
        dragRotate={true} 
      >
        <GeolocateControl onTrackUserLocationStart={handle}  trackUserLocation={true} position="top-right"/>
        <NavigationControl position="top-right" />
        <ScaleControl />
        {/*
        {markers && markers.length && markers.map((item:any, index)=> (
          <Marker key={index} color={item.color??"red"} longitude={item.coord.lng} latitude={item.coord.lat} anchor="center">
            <div className="marker" ><span style={{backgroundColor:item.color??"red"}}><b>{item.name}</b></span></div>
          </Marker>
        ))}       
        {generateFromCoordinates && <Marker color="green" longitude={generateFromCoordinates.lng} latitude={generateFromCoordinates.lat}/>}
        {coords && coords.length && <Source id="route" type="geojson" data={geoJson}>
            <Layer
              id="route"
              type="line"
              layout={{
                'line-join': 'round',
                'line-cap': 'round'
              }}
              paint={{
                'line-color': 'dodgerblue',
                'line-width': 4
              }}
            />
          </Source>}*/}
          {location && <Marker key="location" longitude={location.lng} latitude={location.lat}>
              <div style={{ background:'#ff9300', border:'3px solid #ff9300', borderRadius: '50%',borderBottomRightRadius:0,transform: 'rotate(45deg)translate(-50%, -50%)' }}><Avatar
                  src={user?.photoURL as string}
                  size={"sm"}
                  icon={undefined}
                  bg="black"
                  style={{transform: 'rotate(-45deg)'}}
              /></div>
              </Marker>}
          {mapContent && mapContent}
      </Map>}
      {!location && <>
            <Alert
                status='warning'
                variant='subtle'
                flexDirection='column'
                alignItems='center'
                justifyContent='center'
                textAlign='center'
                height='full'
                position="absolute"
                top={0}
                left={0}   
                color="black"             
                >
                <AlertIcon boxSize='40px' mr={0} />
                <AlertDescription maxWidth='sm'>
                {t("game.gelocalisationNotAuthorized")}
            </AlertDescription>                
            <Button onClick={onOpenGeoloc} colorScheme='orange'>{t("game.activateGeolocalisation")}</Button>
            </Alert>
        </>}
        
      <NoGeolocalisationModal isOpen={isOpenGeoloc} onClose={onCloseGeoloc} />
    </Flex>
  )
}